import React, { useState } from 'react'
import { MdWarning } from 'react-icons/md'
import useResizeObserver from 'use-resize-observer'
import {
  Box,
  BoxProps,
  Button,
  Card,
  CardBody,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure
} from '~/design_system'
import { HOME_QUICK_ACTIONS_QUERY_KEY, useHomeQuickActionsQuery } from '~/api/home'
import { Msg } from '~/common/localizationUtils'
import { queryClient } from '~/common/react-query/queryClient'
import { Ava } from '~/components/ava/Ava'
import { QuickActionButton, QuickActionIcon, QuickActionLink } from '~/components/v2/homepage/QuickAction'

import { MutedSkeleton } from '~/home/common'
import {
  Applied,
  ArrowLeft,
  ArrowRight,
  Ava as AvaIcon,
  Briefcase,
  Checklists,
  Expense,
  FastFill,
  MeetingAccepted,
  Report,
  Rfp,
  TimesheetAlt,
  UserAdd,
  WorkOrders,
  WorkerTracking
} from '~/icons'
import { Icon } from '~/icons/create-icon'
import { JobPublishModal } from '~/job/job_list/components/JobPublishModal'
import { iconColor } from '~/home/BannerGraphic'

const QuickActionSkeleton = (props: BoxProps) => (
  <Card {...props} borderRadius="full" p={3}>
    <Stack isInline>
      <MutedSkeleton height="40px" width="40px" borderRadius="full" />
      <MutedSkeleton height="20px" width="100px" alignSelf="center" />
    </Stack>
  </Card>
)

const DEFAULT_URL = '#'
const FAST_PATH_KEY = 'fast_path'
const CREATE_JOB_KEY = 'create_a_job'
const AVA_KEY = 'ava'

export const QuickActions = ({ defaultSlidesCount = 4, ...props }) => {
  const { data, isLoading, isError } = useHomeQuickActionsQuery()
  const quickActions = isLoading ? Array(defaultSlidesCount).fill(QuickActionSkeleton) : data

  const [slideStartIndex, setSlideStartIndex] = useState(0)
  const totalNumberOfSlides = !isLoading && isError ? 1 : quickActions?.length ?? 0
  const defaultNumberOfActiveSlides = Math.min(defaultSlidesCount, totalNumberOfSlides)

  const { ref, width } = useResizeObserver<HTMLDivElement>()
  // eslint-disable-next-line no-nested-ternary
  const numberOfActiveSlides = width
    ? width > 1103
      ? defaultNumberOfActiveSlides
      : totalNumberOfSlides
    : defaultNumberOfActiveSlides
  const disableNavigation = numberOfActiveSlides === totalNumberOfSlides

  const gridColumns = { base: 1, sm: 2, lg: Math.max(defaultSlidesCount, numberOfActiveSlides) }

  if (!isLoading && isError) {
    return <QuickActionError {...props} />
  }

  return (
    <Box ref={ref} data-quick-actions>
      {totalNumberOfSlides > 0 && (
        <Stack isInline maxW="full" {...props}>
          {!disableNavigation && (
            <IconButton
              aria-label="navigateIconLeft"
              icon={<ArrowLeft />}
              alignSelf="center"
              isDisabled={slideStartIndex - numberOfActiveSlides < 0}
              onClick={() => setSlideStartIndex(slideStartIndex - numberOfActiveSlides)}
              variant="secondary"
              bgColor="white"
              _focus={{ background: 'white' }}
              isRound
            />
          )}
          <SimpleGrid
            data-testid="grid-quick-actions"
            columns={gridColumns}
            w="full"
            p={{ base: 4, lg: 0 }}
            spacing={4}>
            {isLoading
              ? // eslint-disable-next-line react/no-array-index-key
                quickActions?.map((Action: React.ElementType, i: number) => <Action key={i} />)
              : quickActions
                  ?.slice(slideStartIndex, slideStartIndex + numberOfActiveSlides)
                  .map((data: Record<string, any>) => (
                    <Action id={data?.id} key={data?.id} color={iconColor} {...data} />
                  ))}
          </SimpleGrid>
          {!disableNavigation && (
            <IconButton
              aria-label="navigateIconRight"
              icon={<ArrowRight />}
              alignSelf="center"
              isDisabled={slideStartIndex + numberOfActiveSlides >= totalNumberOfSlides}
              onClick={() => setSlideStartIndex(slideStartIndex + numberOfActiveSlides)}
              variant="secondary"
              bgColor="white"
              _focus={{ background: 'white' }}
              isRound
            />
          )}
        </Stack>
      )}
    </Box>
  )
}

const icons: Record<string, typeof Icon> = Object.freeze({
  create_a_job: Briefcase,
  fast_path: FastFill,
  add_a_worker_tracker: WorkerTracking,
  create_sow: Report,
  create_rfp: Rfp,
  review_timesheets: TimesheetAlt,
  review_expenses: Expense,
  ava: AvaIcon,
  approvals: Checklists,
  checklist_actions: Checklists,
  add_candidates: UserAdd,
  apply_candidates: Applied,
  confirm_interview: MeetingAccepted,
  create_worker_profile: WorkOrders
})

type BaseActionProps = {
  id: string
  title?: string
  color: string
  key: any
}
type ActionProps = {
  count?: number
  url?: string
} & BaseActionProps

type AvaActionProps = {
  placement?: string
} & BaseActionProps

const Action = ({ id, title, url, color, count, ...props }: ActionProps & AvaActionProps) => {
  if (id == null) {
    return null
  }
  // AVA works differently compared to the other action items
  if (id === AVA_KEY) {
    return <LaunchAVA id={id} title={title} color={color} {...props} />
  }
  // use this path when feature flag is on for job publish
  if (id === FAST_PATH_KEY || id === CREATE_JOB_KEY) {
    return <LaunchJobPublishModal id={id} title={title} color={color} {...props} />
  }

  return (
    <QuickActionLink
      id={id}
      title={title}
      icon={<QuickActionIcon icon={icons[id]} color={color} />}
      href={url ?? DEFAULT_URL}
      badgeCount={count}
      {...props}
    />
  )
}

const LaunchJobPublishModal = ({ id, title, color, ...props }: BaseActionProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  return (
    <>
      <QuickActionButton
        id={id}
        onClick={onOpen}
        title={title}
        icon={<QuickActionIcon icon={icons[id]} color={color} />}
        {...props}
      />
      <JobPublishModal isOpen={isOpen} onClose={onClose} fastPath={id === FAST_PATH_KEY} />
    </>
  )
}

const LaunchAVA = ({ id, title, placement, color, ...props }: AvaActionProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  return (
    <>
      <QuickActionButton
        id={id}
        onClick={onOpen}
        title={title}
        icon={<QuickActionIcon icon={icons[id]} color={color} />}
        {...props}
      />
      {isOpen && (
        <Ava placement={placement} isOpen={isOpen} onOpen={onOpen} onClose={onClose} openAvaButtonSelector={null} />
      )}
    </>
  )
}

const QuickActionError = (props: BoxProps) => (
  <Card {...props}>
    <CardBody>
      <Stack isInline justifyContent="center" alignItems="center">
        <Box as={MdWarning} color="orange.300" />
        <Text>
          <Msg id="common.ui.content_failed_to_load" />
        </Text>
        <Button variant="link" onClick={() => queryClient.invalidateQueries([HOME_QUICK_ACTIONS_QUERY_KEY])}>
          <Msg id="common.ui.refresh" />
        </Button>
      </Stack>
    </CardBody>
  </Card>
)
